<template>
  <section class="filters" aria-label="Order filters">
    <div class="filters__field">
      <Input
        :model-value="localModel.text"
        label="Fritext"
        id="sales-admin-free-text-filter"
        @update:modelValue="onTextChange"
      />
    </div>

    <div class="filters__field">
      <Input
        :model-value="localModel.dateTime"
        label="Datum"
        type="date"
        id="sales-admin-date-filter"
        @update:modelValue="onDateTimeChange"
      />
    </div>

    <div class="filters__field">
      <Dropdown
        :model-value="localModel.location"
        title="Station"
        id="sales-admin-location-filter"
        :options="resolvedOptions.locations"
        defaultValue="Välj station"
        selectModifier="container-border"
        @update:modelValue="onLocationChange"
      />
    </div>

    <div class="filters__field">
      <Dropdown
        :model-value="localModel.company"
        title="Företagsnamn"
        id="sales-admin-company-filter"
        :options="resolvedOptions.companies"
        defaultValue="Välj företag"
        selectModifier="container-border"
        @update:modelValue="onCompanyChange"
      />
    </div>

    <div class="filters__field">
      <Dropdown
        :model-value="localModel.tripType"
        title="Typ av resa"
        id="sales-admin-trip-type-filter"
        :keyvalueoptions="tripTypeOptions"
        defaultValue="Välj typ av resa"
        selectModifier="container-border"
        @update:modelValue="onTripTypeChange"
      />
    </div>

    <div class="filters__field">
      <BaseCheckbox
        id="sales-admin-show-passed-filter"
        :modelValue="localModel.showPassed"
        :label="'Visa passerade ordrar'"
        :checkboxLabel="'Visa passerade ordrar'"
        rootClassModifier="base-checkbox--no-padding"
        @update:modelValue="onShowPassedChange"
      />
    </div>

    <div class="filters__actions">
      <button type="button" class="button button--link filters__reset" @click="reset">
        Rensa filter
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { getTripTypeOptions } from "@features/Pages/SalesAdminPage/Services/trip-type-options";
import BaseCheckbox from "@shared/Components/Checkbox/BaseCheckbox.vue";
import Dropdown from "@shared/Components/Dropdown/Dropdown.vue";
import Input from "@shared/Components/Input/Input.vue";
import { FiltersOptions } from "@components/Filters/FiltersOptions";

const props = defineProps<{
  modelValue?: FiltersModel;
  options?: Partial<FiltersOptions>;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: FiltersModel): void;
  (e: "change", value: FiltersModel): void;
  (e: "reset", value: FiltersModel): void;
}>();

const localModel = ref<FiltersModel>(createModel(props.modelValue));
const resolvedOptions = computed<FiltersOptions>(() => ({
  locations: props.options?.locations ?? [],
  companies: props.options?.companies ?? [],
  tripTypes: props.options?.tripTypes ?? [],
}));
const tripTypeOptions = computed(() => getTripTypeOptions(resolvedOptions.value.tripTypes));

watch(
  () => props.modelValue,
  (value) => {
    localModel.value = createModel(value);
  },
  { deep: true },
);

function onTextChange(value: string): void {
  updateModel({
    ...localModel.value,
    text: value.trim(),
  });
}

function onDateTimeChange(value: string): void {
  updateModel({
    ...localModel.value,
    dateTime: value,
  });
}

function onLocationChange(value: string): void {
  updateModel({
    ...localModel.value,
    location: normalizeNullableValue(value),
  });
}

function onCompanyChange(value: string): void {
  updateModel({
    ...localModel.value,
    company: normalizeNullableValue(value),
  });
}

function onTripTypeChange(value: string): void {
  updateModel({
    ...localModel.value,
    tripType: normalizeNullableValue(value),
  });
}

function onShowPassedChange(value: boolean): void {
  updateModel({
    ...localModel.value,
    showPassed: value,
  });
}

function reset(): void {
  const emptyModel = createEmptyModel();
  localModel.value = emptyModel;
  emit("update:modelValue", emptyModel);
  emit("reset", emptyModel);
}

function updateModel(value: FiltersModel): void {
  localModel.value = value;
  emit("update:modelValue", value);
  emit("change", value);
}

function createModel(value?: Partial<FiltersModel>): FiltersModel {
  return {
    text: value?.text ?? "",
    dateTime: value?.dateTime ?? "",
    location: value?.location ?? null,
    company: value?.company ?? null,
    tripType: value?.tripType ?? null,
    showPassed: value?.showPassed ?? false,
  };
}

function createEmptyModel(): FiltersModel {
  return {
    text: "",
    dateTime: "",
    location: null,
    company: null,
    tripType: null,
    showPassed: false,
  };
}

function normalizeNullableValue(value: string): string | null {
  const trimmedValue = value.trim();
  return trimmedValue === "" ? null : trimmedValue;
}
</script>
